import { useCallback, useEffect } from 'react';
import { useBusinessEntity } from '@atlassian/jira-business-entity-common/src/controllers/business-entity-context/index.tsx';
import { useIssueEvents } from '@atlassian/jira-business-issue-events-observable/src/index.tsx';
import { createStore, createHook } from '@atlassian/react-sweet-state';
import { useUnscheduledItemsCount as useUnscheduledItemsCountService } from '../../services/unscheduled-items-count/index.tsx';

type State = {
	count: number;
	entityId: string;
};

type StoreAPI = {
	setState: (state: State) => void;
	getState: () => State;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	dispatch: (arg1?: any) => void;
};

const initEntity =
	(entityId: string) =>
	({ setState }: StoreAPI) => {
		setState({ count: 0, entityId });
	};

const setCount =
	(count: number) =>
	({ getState, setState }: StoreAPI) => {
		setState({ ...getState(), count });
	};

const decrementCount =
	() =>
	({ getState, setState }: StoreAPI) => {
		// prevent count from ever going below 0
		const count = Math.max(0, getState().count - 1);
		setState({ ...getState(), count });
	};

const actions = { initEntity, setCount, decrementCount };

const initialState: State = {
	count: 0,
	entityId: '',
};

const Store = createStore({
	initialState,
	actions,
	name: 'JWMUnscheduledItemsCount',
});

const useStore = createHook(Store);

export const useUnscheduledItemsCount = () => {
	const { data: entity } = useBusinessEntity();
	const [{ count, entityId }, actors] = useStore();

	const fetchCount = useUnscheduledItemsCountService();

	useEffect(() => {
		const fetch = async () => {
			if (entity == null || entityId === entity.id) {
				return;
			}

			try {
				actors.initEntity(entity.id);
				const total = await fetchCount();
				actors.setCount(total);
			} catch (error) {
				// do nothing
			}
		};

		fetch();
	}, [actors, entity, entityId, fetchCount]);

	const updateCount = useCallback(async () => {
		if (entity == null) {
			return;
		}

		try {
			const total = await fetchCount();
			actors.setCount(total);
		} catch (error) {
			// do nothing
		}
	}, [actors, entity, fetchCount]);

	// Update count on real time events
	useIssueEvents(updateCount);

	return count;
};

export const useDecrementUnscheduledItemsCount = () => {
	const [, actors] = useStore();

	return actors.decrementCount;
};
